import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, setAccessToken } from '../../services/api';
import { useAuthStore } from '../../services/useAuthStore';
import './Register.css';

const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

const validatePasswordStrength = (password) => {
  if (!password) return { valid: false, message: 'Senha é obrigatória' };
  if (password.length < 8) {
    return { valid: false, message: 'A senha deve ter no mínimo 8 caracteres' };
  }
  if (!/[A-Z]/.test(password)) {
    return { valid: false, message: 'A senha deve conter pelo menos uma letra maiúscula' };
  }
  if (!/[a-z]/.test(password)) {
    return { valid: false, message: 'A senha deve conter pelo menos uma letra minúscula' };
  }
  if (!/[0-9]/.test(password)) {
    return { valid: false, message: 'A senha deve conter pelo menos um número' };
  }
  return { valid: true, message: 'Senha forte' };
};

const sanitizeString = (str) => {
  return str.replace(/[<>]/g, '');
};


const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const Register = () => {
  const navigate = useNavigate();
  const setAuthFromLogin = useAuthStore((s) => s.setAuthFromLogin);
  const switchWorkspace = useAuthStore((s) => s.switchWorkspace);
  const visualSectionRef = useRef(null);

 
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    workspaceName: '',
    workspaceSlug: '',
    agreedToTerms: false
  });

 
  const [errors, setErrors] = useState({});
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(null);


  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);


  const handleMouseMove = (e) => {
    if (!visualSectionRef.current) return;
    const { left, top, width, height } = visualSectionRef.current.getBoundingClientRect();
    const x = (e.clientX - left) / width;
    const y = (e.clientY - top) / height;

    visualSectionRef.current.style.setProperty("--mouse-x", x);
    visualSectionRef.current.style.setProperty("--mouse-y", y);
  };


  useEffect(() => {
    if (formData.workspaceName) {
      const generatedSlug = formData.workspaceName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setFormData(prev => ({ ...prev, workspaceSlug: generatedSlug }));
    }
  }, [formData.workspaceName]);


  useEffect(() => {
    if (formData.workspaceSlug.length >= 3) {
      const debouncedCheck = debounce(checkSlugAvailability, 500);
      debouncedCheck(formData.workspaceSlug);
    } else {
      setSlugAvailable(null);
    }
  }, [formData.workspaceSlug]);

  const checkSlugAvailability = async (slug) => {
    setCheckingSlug(true);
    try {
      const response = await api.get(`/api/auth/check-slug?slug=${slug}`);
      setSlugAvailable(response.data.available);
    } catch (error) {
      console.error('Erro ao verificar slug:', error);
      setSlugAvailable(null);
    } finally {
      setCheckingSlug(false);
    }
  };


  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null }));


    if (field === 'password') {
      const strength = validatePasswordStrength(value);
      setPasswordStrength(strength);
    }
  };


  const validate = () => {
    const newErrors = {};

    if (!formData.name || formData.name.length < 2) {
      newErrors.name = 'O nome deve ter no mínimo 2 caracteres';
    }

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Formato de email inválido';
    }

    if (!passwordStrength || !passwordStrength.valid) {
      newErrors.password = passwordStrength?.message || 'A senha não atende aos requisitos';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'As senhas não coincidem';
    }

    if (!formData.workspaceName || formData.workspaceName.length < 2) {
      newErrors.workspaceName = 'O nome do workspace deve ter no mínimo 2 caracteres';
    }

    if (!formData.workspaceSlug || formData.workspaceSlug.length < 3) {
      newErrors.workspaceSlug = 'O slug do workspace deve ter no mínimo 3 caracteres';
    }

    if (!slugAvailable) {
      newErrors.workspaceSlug = 'Este slug não está disponível';
    }

    if (!formData.agreedToTerms) {
      newErrors.agreedToTerms = 'Você deve concordar com os Termos de Serviço';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

 
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);
    setSubmitError(null);

    try {

      const sanitizedData = {
        admin_name: sanitizeString(formData.name.trim()),
        admin_email: formData.email.toLowerCase().trim(),
        admin_password: formData.password,
        workspace_name: sanitizeString(formData.workspaceName.trim()),
        workspace_slug: formData.workspaceSlug.toLowerCase().trim()
      };

  
      const response = await api.post('/api/auth/register', sanitizedData);

      // Setar o access_token
      if (response.data.access_token) {
        setAccessToken(response.data.access_token);
      }

      // Usar novo fluxo de autenticacao
      setAuthFromLogin(response.data);

      // Novo usuario sempre tem 1 workspace (o que acabou de criar)
      // Fazer switch para ativar o workspace
      const workspaces = response.data.workspaces || [];
      if (workspaces.length > 0) {
        await switchWorkspace(workspaces[0].id);
      }

      // Redirecionar para home (modal de ativacao sera exibido la)
      navigate('/');
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Falha no registro. Tente novamente.';
      setSubmitError(errorMessage);

   
      if (errorMessage.includes('email') || errorMessage.includes('E-mail')) {
        setErrors(prev => ({ ...prev, email: errorMessage }));
      } else if (errorMessage.includes('slug') || errorMessage.includes('Slug')) {
        setErrors(prev => ({ ...prev, workspaceSlug: errorMessage }));
      }
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return (
      formData.name &&
      validateEmail(formData.email) &&
      passwordStrength?.valid &&
      formData.password === formData.confirmPassword &&
      formData.workspaceName &&
      formData.workspaceSlug &&
      slugAvailable &&
      formData.agreedToTerms
    );
  };

  const getPasswordStrengthClass = () => {
    if (!passwordStrength) return '';
    return passwordStrength.valid ? 'strength-strong' : 'strength-weak';
  };

  return (
    <div className="login-page">
      <div className="login-split-screen">
        <div
          className="login-visual-section"
          ref={visualSectionRef}
          onMouseMove={handleMouseMove}
        >
          <div className="tech-background base-layer">
            <div className="tech-grid"></div>
            <div className="tech-glow"></div>
          </div>

          <div className="tech-background reveal-layer">
            <div className="tech-grid active"></div>
            <div className="tech-glow active"></div>
          </div>

          <div className="login-image-content">
            <h2>Junte-se ao Futuro</h2>
            <p>Crie sua conta e comece a transformar a gestão da sua equipe hoje mesmo.</p>
          </div>
        </div>


        <div className="login-form-section">
          <div className="login-container">
            <div className="login-header">
              <img src="/logoFastTeamGreen.png" alt="FastTeam Logo" className="login-logo" style={{ height: "40px" }} />
              <h2 className="login-title">Criar Conta</h2>
              <p className="login-subtitle">Comece agora</p>
            </div>

            {submitError && (
              <div className="login-error">{submitError}</div>
            )}

            <form className="login-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Nome Completo *</label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className={`form-control ${errors.name ? 'error' : ''}`}
                  placeholder="Seu nome"
                  required
                />
                {errors.name && <div className="error-text">{errors.name}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className={`form-control ${errors.email ? 'error' : ''}`}
                  placeholder="seu@email.com"
                  required
                />
                {errors.email && <div className="error-text">{errors.email}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="password">Senha *</label>
                <input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  className={`form-control ${errors.password ? 'error' : ''}`}
                  placeholder="••••••••"
                  required
                />
                {errors.password && <div className="error-text">{errors.password}</div>}
                {formData.password && (
                  <div className={`password-strength ${getPasswordStrengthClass()}`}>
                    {passwordStrength?.message}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirmar Senha *</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  className={`form-control ${errors.confirmPassword ? 'error' : ''}`}
                  placeholder="••••••••"
                  required
                />
                {errors.confirmPassword && <div className="error-text">{errors.confirmPassword}</div>}
                {formData.password && formData.password === formData.confirmPassword && formData.confirmPassword && (
                  <div className="match-indicator">✓ Senhas coincidem</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="workspaceName">Nome do Workspace *</label>
                <input
                  id="workspaceName"
                  type="text"
                  value={formData.workspaceName}
                  onChange={(e) => handleChange('workspaceName', e.target.value)}
                  className={`form-control ${errors.workspaceName ? 'error' : ''}`}
                  placeholder="ex: Minha Empresa"
                  required
                />
                {errors.workspaceName && <div className="error-text">{errors.workspaceName}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="workspaceSlug">Slug do Workspace *</label>
                <input
                  id="workspaceSlug"
                  type="text"
                  value={formData.workspaceSlug}
                  onChange={(e) => handleChange('workspaceSlug', e.target.value)}
                  className={`form-control ${errors.workspaceSlug ? 'error' : ''}`}
                  placeholder="ex: minha-empresa"
                  required
                />
                {errors.workspaceSlug && <div className="error-text">{errors.workspaceSlug}</div>}
                {formData.workspaceSlug.length >= 3 && (
                  <div className="slug-status">
                    {checkingSlug && <span>Verificando...</span>}
                    {!checkingSlug && slugAvailable === true && (
                      <span className="slug-available">✓ Disponível</span>
                    )}
                    {!checkingSlug && slugAvailable === false && (
                      <span className="slug-unavailable">✗ Não disponível</span>
                    )}
                  </div>
                )}
                <small className="helper-text">Este será parte da URL do seu workspace</small>
              </div>


              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.agreedToTerms}
                    onChange={(e) => handleChange('agreedToTerms', e.target.checked)}
                  />
                  <span>
                    Concordo com os <Link to="/terms">Termos de Serviço</Link> e{' '}
                    <Link to="/privacy">Política de Privacidade</Link>
                  </span>
                </label>
                {errors.agreedToTerms && <div className="error-text">{errors.agreedToTerms}</div>}
              </div>

              <button
                type="submit"
                className="btn-login"
                disabled={!isFormValid() || loading}
              >
                {loading ? 'Criando Conta...' : 'Criar Conta'}
              </button>

              <Link to="/login" className="btn-link-inline">
                Já tem uma conta? Fazer login
              </Link>
            </form>

            <div className="login-footer">
              <p>© 2025 Fasteam. All rights reserved.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
